// HEAL-76 — лента уведомлений.
//
// Счётчика непрочитанных здесь нет: он приходит с профилем (D-7), и отдельный
// запрос ради него был бы вторым источником одного числа.

import {
  useInfiniteQuery,
  useMutation,
  useQueryClient,
  type UseInfiniteQueryResult,
  type UseMutationResult,
} from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { api } from '@/api/client';
import { queryKeys } from '@/api/queryKeys';
import type {
  ApiError,
  MarkNotificationsReadResponse,
  NotificationsResponse,
} from '@/api/types';

export function useNotifications(): UseInfiniteQueryResult<
  { pages: NotificationsResponse[]; pageParams: number[] },
  AxiosError<ApiError>
> {
  return useInfiniteQuery({
    queryKey: queryKeys.notifications,
    queryFn: async ({ pageParam }) => {
      const { data } = await api.get<NotificationsResponse>('/notifications', {
        params: { page: pageParam },
      });
      return data;
    },
    initialPageParam: 1,
    getNextPageParam: (last) =>
      last.meta.current_page < last.meta.last_page ? last.meta.current_page + 1 : undefined,

    // ⚠️ Общие настройки кэша этому экрану не подходят — ровно как экрану пакета.
    //
    // По умолчанию данные считаются свежими 30 секунд, и повторное открытие
    // ленты внутри этого окна отдаёт старый список БЕЗ запроса. А счётчик на
    // колокольчике живёт в профиле и сбрасывается принудительно — при тапе по
    // push и при возврате приложения из фона. Асимметрия и давала «цифра есть,
    // сообщения нет»: бейдж уже обновился, а список показывался прошлый.
    //
    // Содержимое ленты меняется на сервере само, и держать его «свежим» по
    // таймеру бессмысленно: единственный достоверный ответ — только что
    // полученный.
    staleTime: 0,
    refetchOnMount: 'always',
  });
}

/**
 * Отметка о прочтении — по идентификаторам показанных записей (D-6).
 *
 * НЕ «пометить всё»: при ста непрочитанных человек видит двадцать, и остальные
 * восемьдесят он не читал. Ответ приносит реальный остаток, которым и
 * обновляется счётчик в профиле — без повторного запроса.
 */
export function useMarkNotificationsRead(): UseMutationResult<
  MarkNotificationsReadResponse,
  AxiosError<ApiError>,
  number[]
> {
  const qc = useQueryClient();

  return useMutation({
    mutationFn: async (ids: number[]) => {
      const { data } = await api.post<MarkNotificationsReadResponse>('/notifications/read', {
        ids,
      });
      return data;
    },
    // Мутации по умолчанию не повторяются, а эта идемпотентна по построению:
    // повторная отметка тех же записей ничего не меняет. Обрыв связи на секунду
    // не должен оставлять человека с бейджем, который он уже погасил.
    //
    // 429 не повторяем: у эндпоинта лимит 30/мин, и долбить его бессмысленно —
    // отметка уйдёт при следующем открытии ленты.
    retry: (failureCount, error) => error?.response?.status !== 429 && failureCount < 2,
    onSuccess: (data) => {
      qc.setQueryData(queryKeys.profileMe, (prev: unknown) => {
        if (!prev || typeof prev !== 'object') return prev;
        const profile = prev as { meta?: Record<string, unknown> };
        if (!profile.meta) return prev;

        return { ...profile, meta: { ...profile.meta, unread_notifications: data.unread } };
      });

      void qc.invalidateQueries({ queryKey: queryKeys.notifications });
    },
  });
}
